function updateHeaderCartBadges(itemCount) {
  const count = Number(itemCount || 0);
  document.querySelectorAll('[data-cart-count]').forEach((badge) => {
    badge.textContent = count;
    badge.classList.toggle('is-empty', count === 0);
  });
}

function formatCartDrawerMoney(amount, currency = 'USD') {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency,
    }).format(Number(amount || 0) / 100);
  } catch {
    return `$${(Number(amount || 0) / 100).toFixed(2)}`;
  }
}

function escapeCartDrawerHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => {
      const recommendationButton = event.target.closest('[data-cart-drawer-recommendation-add]');
      if (recommendationButton) this.addRecommendation(recommendationButton);
    });
    if (typeof subscribe === 'function') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (typeof event?.cartData?.item_count === 'number') updateHeaderCartBadges(event.cartData.item_count);
      });
    }
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach((cartLink) => {
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('#CartDrawer');
        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.loadRecommendations();

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  replaceSection(sectionHTML) {
    const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextDrawer = html.querySelector('cart-drawer');
    const nextContent = html.querySelector('#CartDrawer');
    const currentContent = this.querySelector('#CartDrawer');
    if (!nextDrawer || !nextContent || !currentContent) return false;

    this.classList.toggle('is-empty', nextDrawer.classList.contains('is-empty'));
    currentContent.innerHTML = nextContent.innerHTML;
    updateHeaderCartBadges(this.querySelector('.po-cart-item-count')?.textContent.trim());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    if (this.classList.contains('active')) {
      const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
      if (focusElement) trapFocus(currentContent, focusElement);
      if (!this.classList.contains('is-empty')) this.loadRecommendations();
    }
    return true;
  }

  async loadRecommendations() {
    const section = this.querySelector('[data-cart-drawer-recommendations]');
    const track = this.querySelector('[data-cart-drawer-recommendation-track]');
    if (!section || !track || section.dataset.loaded === 'true') return;

    try {
      const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
      const productId = cart.items?.[0]?.product_id;
      if (!productId) return;

      const fetchRecommendations = (intent) => fetch(
        `${routes.root}recommendations/products.json?product_id=${productId}&limit=4&intent=${intent}`,
        { headers: { Accept: 'application/json' } },
      ).then((response) => (response.ok ? response.json() : { products: [] }));

      let recommendationData = await fetchRecommendations('complementary');
      if (!recommendationData.products?.length) recommendationData = await fetchRecommendations('related');

      const cartProductIds = new Set(cart.items.map((item) => item.product_id));
      const cards = (recommendationData.products || [])
        .filter((product) => !cartProductIds.has(product.id))
        .slice(0, 4)
        .map((product) => {
          const variant = product.variants?.find((candidate) => candidate.available) || product.variants?.[0];
          if (!variant) return '';
          const image = typeof product.featured_image === 'string' ? product.featured_image : product.featured_image?.url;
          const imageMarkup = image
            ? `<img src="${escapeCartDrawerHtml(image)}" alt="${escapeCartDrawerHtml(product.title)}" loading="lazy">`
            : '';
          return `
            <article class="po-cart-recommendation">
              ${imageMarkup}
              <div class="po-cart-recommendation__copy">
                <a href="${escapeCartDrawerHtml(product.url)}">${escapeCartDrawerHtml(product.title)}</a>
                <p class="po-cart-recommendation__note">Pair it with your routine.</p>
                <div class="po-cart-recommendation__bottom">
                  <span>${escapeCartDrawerHtml(formatCartDrawerMoney(variant.price, cart.currency))}</span>
                  <button type="button" data-cart-drawer-recommendation-add data-variant-id="${escapeCartDrawerHtml(variant.id)}"${variant.available ? '' : ' disabled'}>ADD</button>
                </div>
              </div>
            </article>`;
        })
        .filter(Boolean)
        .join('');

      if (!cards) return;
      track.innerHTML = cards;
      section.hidden = false;
      section.dataset.loaded = 'true';
    } catch {
      section.hidden = true;
    }
  }

  async addRecommendation(button) {
    const variantId = Number(button.dataset.variantId);
    if (!variantId || button.disabled) return;

    const originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'ADDING…';

    try {
      const response = await fetch(routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const parsedState = await response.json();
      if (!response.ok) throw new Error(parsedState.description || 'Unable to add this item.');
      this.renderContents(parsedState);
    } catch {
      button.textContent = originalLabel;
      button.disabled = false;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    const sectionHTML = parsedState.sections?.['cart-drawer'];
    if (!sectionHTML || !this.replaceSection(sectionHTML)) return;
    if (!this.classList.contains('active')) this.open();
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
